import { useMemo } from "react";
import UserMedal from "../interfaces/userMedal";
import "./DashboardData.css";

const CHART_WIDTH = 920;
const CHART_HEIGHT = 260;
const PADDING = { top: 20, right: 24, bottom: 38, left: 46 };

const formatChartDate = (value: string) => {
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year.slice(2)}`;
};

const LINE_COLORS = [
  "#ff7a45",
  "#4dd0e1",
  "#a5d6a7",
  "#ffd54f",
  "#f48fb1",
  "#90caf9",
  "#ffab91",
  "#ce93d8",
];

const normalizeDateKey = (createdAt: Date) => {
  const raw = String(createdAt);
  if (raw.includes("T")) return raw.split("T")[0];

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const DashboardData = ({ data }: { data: UserMedal[] | undefined }) => {
  const chartData = useMemo(() => {
    const allDates = new Set<string>();
    const seriesMap = new Map<string, Map<string, number>>();

    data?.forEach((item) => {
      const dateKey = normalizeDateKey(item.createdAt);
      if (!dateKey) return;

      const medalName = item.medal?.name ?? "Unknown medal";
      allDates.add(dateKey);

      if (!seriesMap.has(medalName)) {
        seriesMap.set(medalName, new Map<string, number>());
      }

      const medalByDate = seriesMap.get(medalName)!;
      const currentValue = medalByDate.get(dateKey) ?? 0;
      medalByDate.set(dateKey, currentValue + item.quantity);
    });

    const sortedDates = Array.from(allDates).sort((a, b) => a.localeCompare(b));

    const series = Array.from(seriesMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([medalName, valuesByDate], index) => ({
        medalName,
        color: LINE_COLORS[index % LINE_COLORS.length],
        values: sortedDates.map((date) => ({
          date,
          total: Number((valuesByDate.get(date) ?? 0).toFixed(2)),
        })),
      }));

    return { dates: sortedDates, series };
  }, [data]);

  const totalQuantity = useMemo(
    () =>
      chartData.series.reduce(
        (acc, serie) =>
          acc + serie.values.reduce((sum, point) => sum + point.total, 0),
        0,
      ),
    [chartData],
  );

  if (!data || data.length === 0) {
    return (
      <section className="dashboard-data-card" aria-label="Dashboard data">
        <h2>Dashboard Data</h2>
        <p className="dashboard-data-empty">
          No history data found for the selected filters.
        </p>
      </section>
    );
  }

  if (chartData.dates.length === 0 || chartData.series.length === 0) {
    return (
      <section className="dashboard-data-card" aria-label="Dashboard data">
        <h2>Dashboard Data</h2>
        <p className="dashboard-data-empty">
          Unable to build chart from received dates.
        </p>
      </section>
    );
  }

  const chartInnerWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  const chartInnerHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;

  const maxValue = Math.max(
    ...chartData.series.flatMap((serie) =>
      serie.values.map((point) => point.total),
    ),
    1,
  );
  const xStep =
    chartData.dates.length > 1
      ? chartInnerWidth / (chartData.dates.length - 1)
      : 0;

  const datePoints = chartData.dates.map((date, index) => ({
    date,
    x: PADDING.left + index * xStep,
  }));

  const plottedSeries = chartData.series.map((serie) => {
    const points = serie.values.map((value, index) => {
      const x = PADDING.left + index * xStep;
      const y = PADDING.top + chartInnerHeight * (1 - value.total / maxValue);
      return { ...value, x, y };
    });

    return {
      ...serie,
      points,
      polyline: points.map((point) => `${point.x},${point.y}`).join(" "),
    };
  });

  const xAxisLabels = datePoints.filter(
    (_, index) =>
      index === 0 ||
      index === datePoints.length - 1 ||
      index % Math.max(1, Math.floor(datePoints.length / 5)) === 0,
  );

  return (
    <section className="dashboard-data-card" aria-label="Dashboard data">
      <div className="dashboard-data-header">
        <h2>Dashboard Data</h2>
        <div className="dashboard-data-summary">
          <span>{chartData.dates.length} day(s)</span>
          <span>{chartData.series.length} medal line(s)</span>
          <span>{totalQuantity.toFixed(2)} total quantity</span>
        </div>
      </div>

      <div className="dashboard-chart-legend">
        {plottedSeries.map((serie) => (
          <span key={serie.medalName} className="dashboard-legend-item">
            <i style={{ backgroundColor: serie.color }} />
            {serie.medalName}
          </span>
        ))}
      </div>

      <div className="dashboard-chart-wrap">
        <svg
          className="dashboard-chart"
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          role="img"
          aria-label="Daily medal quantity trend"
          preserveAspectRatio="xMidYMid meet"
        >
          <line
            x1={PADDING.left}
            y1={PADDING.top + chartInnerHeight}
            x2={CHART_WIDTH - PADDING.right}
            y2={PADDING.top + chartInnerHeight}
            className="dashboard-axis"
          />
          <line
            x1={PADDING.left}
            y1={PADDING.top}
            x2={PADDING.left}
            y2={PADDING.top + chartInnerHeight}
            className="dashboard-axis"
          />

          {plottedSeries.map((serie) => (
            <g key={`serie-${serie.medalName}`}>
              <polyline
                points={serie.polyline}
                className="dashboard-line"
                style={{ stroke: serie.color }}
              />

              {serie.points.map((point) => (
                <circle
                  key={`point-${serie.medalName}-${point.date}`}
                  cx={point.x}
                  cy={point.y}
                  r={3.5}
                  className="dashboard-point"
                  style={{ fill: serie.color }}
                >
                  <title>
                    {serie.medalName} - {formatChartDate(point.date)} -{" "}
                    {point.total}
                  </title>
                </circle>
              ))}
            </g>
          ))}

          {xAxisLabels.map((point) => (
            <text
              key={`label-${point.date}`}
              x={point.x}
              y={CHART_HEIGHT - 10}
              textAnchor="middle"
              className="dashboard-axis-label"
            >
              {formatChartDate(point.date)}
            </text>
          ))}

          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = PADDING.top + chartInnerHeight * (1 - ratio);
            const value = (maxValue * ratio).toFixed(1);
            return (
              <g key={`grid-${ratio}`}>
                <line
                  x1={PADDING.left}
                  y1={y}
                  x2={CHART_WIDTH - PADDING.right}
                  y2={y}
                  className="dashboard-grid"
                />
                <text
                  x={PADDING.left - 8}
                  y={y + 4}
                  textAnchor="end"
                  className="dashboard-axis-label"
                >
                  {value}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </section>
  );
};

export default DashboardData;
